/**
 * Utilities for normalizing phone numbers and WhatsApp links across the application.
 */

/**
 * Normalizes any input phone/WA number to international format without leading zero or spaces.
 * Example: '0812-3456-7890' -> '6281234567890'
 * Example: '+62 812 3456 7890' -> '6281234567890'
 * Example: '6281234567890' -> '6281234567890'
 */
export function formatWaNumber(rawNumber: string | undefined | null): string {
  if (!rawNumber) return '6281234567890';
  let cleaned = rawNumber.replace(/[^0-9]/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = '62' + cleaned.slice(1);
  }
  return cleaned || '6281234567890';
}

/**
 * Builds a valid WhatsApp Web / API click URL with target message.
 */
export function buildWaUrl(rawNumber: string | undefined | null, defaultMessage?: string): string {
  const waNumber = formatWaNumber(rawNumber);
  const text = encodeURIComponent(
    defaultMessage || 'Halo Admin Nusantara, saya tertarik dengan layanan Anda.'
  );
  return `https://wa.me/${waNumber}?text=${text}`;
}

/**
 * Formats a telephone number for tel: protocol URI without invalid spaces or hyphens.
 * Example: '0812-3456-7890' -> '+6281234567890'
 * Example: '+62 812 3456 7890' -> '+6281234567890'
 */
export function formatPhoneTel(rawNumber: string | undefined | null): string {
  if (!rawNumber) return '+6281234567890';
  let cleaned = rawNumber.trim().replace(/[^0-9+]/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = '+62' + cleaned.slice(1);
  } else if (!cleaned.startsWith('+') && cleaned.startsWith('62')) {
    cleaned = '+' + cleaned;
  }
  return cleaned || '+6281234567890';
}

/**
 * Parses and converts any Google Maps input (iframe HTML snippet, share link, place URL, short link, or address string)
 * into a valid, embeddable Google Maps iframe URL that works inside an <iframe> without X-Frame-Options or search errors.
 */
export function parseGoogleMapsEmbedUrl(input: string | undefined | null, fallbackAddress?: string): string {
  const addressQuery = fallbackAddress?.trim() || 'Lembang, Bandung';
  const defaultEmbed = `https://maps.google.com/maps?q=${encodeURIComponent(addressQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

  if (!input || !input.trim()) {
    return defaultEmbed;
  }

  let trimmed = input.trim();

  // 1. Extract src if user pasted an entire <iframe>...</iframe> HTML snippet
  const iframeSrcMatch = trimmed.match(/src=["']([^"']+)["']/i);
  if (iframeSrcMatch && iframeSrcMatch[1]) {
    trimmed = iframeSrcMatch[1].trim();
  }

  // 2. If already a valid embed URL (google.com/maps/embed or maps.google.com/maps with output=embed)
  if (
    trimmed.includes('google.com/maps/embed') ||
    (trimmed.includes('maps.google.com/maps') && trimmed.includes('output=embed'))
  ) {
    return trimmed;
  }

  // 3. Extract place name if user pasted a Google Maps place URL (e.g. https://www.google.com/maps/place/Bellva+Outbound/...)
  if (trimmed.includes('/maps/place/')) {
    const placePart = trimmed.split('/maps/place/')[1];
    if (placePart) {
      const placeSegment = placePart.split('/')[0].split('?')[0];
      if (placeSegment) {
        try {
          const decodedPlace = decodeURIComponent(placeSegment.replace(/\+/g, ' ')).trim();
          if (decodedPlace) {
            return `https://maps.google.com/maps?q=${encodeURIComponent(decodedPlace)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
          }
        } catch (e) {
          // Ignore
        }
      }
    }
  }

  // 4. Extract search query if user pasted a search URL (/maps/search/...)
  if (trimmed.includes('/maps/search/')) {
    const searchPart = trimmed.split('/maps/search/')[1];
    if (searchPart) {
      const searchSegment = searchPart.split('/')[0].split('?')[0];
      if (searchSegment) {
        try {
          const decodedSearch = decodeURIComponent(searchSegment.replace(/\+/g, ' ')).trim();
          if (decodedSearch) {
            return `https://maps.google.com/maps?q=${encodeURIComponent(decodedSearch)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
          }
        } catch (e) {
          // Ignore
        }
      }
    }
  }

  // 5. Extract coordinates if URL contains @lat,lng e.g. /@-6.818293,107.618293
  const coordsMatch = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (coordsMatch && coordsMatch[1] && coordsMatch[2]) {
    return `https://maps.google.com/maps?q=${coordsMatch[1]},${coordsMatch[2]}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  }

  // 6. Extract q= parameter if present in URL (e.g. ?q=-6.818,107.618 or ?q=Bellva)
  if (trimmed.includes('?q=') || trimmed.includes('&q=')) {
    try {
      const queryString = trimmed.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      const qParam = params.get('q');
      if (qParam && !qParam.startsWith('http')) {
        return `https://maps.google.com/maps?q=${encodeURIComponent(qParam)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
      }
    } catch (e) {
      // Ignore
    }
  }

  // 7. If user pasted a short link (maps.app.goo.gl or goo.gl/maps) or generic URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    if (fallbackAddress && fallbackAddress.trim()) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(fallbackAddress.trim())}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
    }
  }

  // 8. If user typed plain address text or place name directly into the map field
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(trimmed)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  }

  return defaultEmbed;
}

